// Geocoding Service with Firestore Caching
// Uses Photon API (by Komoot) — OpenStreetMap data, CORS-enabled for browser use

import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

export interface GeocodingResult {
  lat: number;
  lng: number;
  address: string;
  cachedAt?: Date;
}

/**
 * Simple hash function for cache keys
 */
function hashAddress(address: string): string {
  return address
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '_')
    .substring(0, 100);
}

/**
 * Build a readable address string from a Photon GeoJSON feature.
 * Defensive: returns 'Onbekend adres' if props is missing or empty.
 */
function buildAddressFromPhoton(props: Record<string, any> | null | undefined): string {
  if (!props || typeof props !== 'object') return 'Onbekend adres';
  const parts: string[] = [];
  if (props.name) parts.push(String(props.name));
  if (props.street) {
    parts.push(props.housenumber
      ? `${props.street} ${props.housenumber}`
      : String(props.street));
  }
  if (props.postcode) parts.push(String(props.postcode));
  if (props.city) parts.push(String(props.city));
  if (props.country) parts.push(String(props.country));
  return parts.join(', ') || (props.display_name ? String(props.display_name) : 'Onbekend adres');
}

/** HTTP status codes from Photon that indicate rate limiting or bad input.
 *  For these we fall back to Nominatim. For 5xx server errors we don't —
 *  Nominatim would likely fail for the same reason. */
const PHOTON_FALLBACK_STATUSES = new Set([400, 429]);

/**
 * Fallback geocoder using OpenStreetMap Nominatim.
 * Used when Photon is rate-limited (400/429).
 * Nominatim enforces a strict 1 req/sec policy — we add a small delay before
 * calling so rapid debounced searches don't exceed the quota.
 */
async function geocodeViaNominatim(address: string): Promise<GeocodingResult | null> {
  // Respect Nominatim's 1 req/sec usage policy with a short delay
  await new Promise(resolve => setTimeout(resolve, 250));
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1&countrycodes=nl,be&accept-language=nl`;
    const response = await fetch(url, {
      headers: { 'Accept-Language': 'nl' }
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    const item = data[0];
    const lat = parseFloat(item.lat);
    const lng = parseFloat(item.lon);
    // Validate parsed coordinates before returning
    if (isNaN(lat) || isNaN(lng)) return null;
    return {
      lat,
      lng,
      address: item.display_name || address,
    };
  } catch {
    return null;
  }
}

/**
 * Geocode address to coordinates with Firestore caching.
 * Primary: Photon (komoot) — fast, CORS-enabled, no API key.
 * Fallback: Nominatim (OpenStreetMap) — used when Photon is rate-limited (400/429).
 * Cache expires: 30 days.
 */
export async function geocodeAddress(address: string): Promise<GeocodingResult | null> {
  if (!address || address.trim().length < 2) {
    return null;
  }

  const addressHash = hashAddress(address);

  try {
    // 1. Check Firestore cache first
    const cacheRef = doc(db, 'geocodingCache', addressHash);
    const cacheDoc = await getDoc(cacheRef);

    if (cacheDoc.exists()) {
      const cached = cacheDoc.data();
      const cachedAt = cached.cachedAt?.toDate();
      const daysSinceCached = cachedAt
        ? (Date.now() - cachedAt.getTime()) / (1000 * 60 * 60 * 24)
        : 999;

      if (daysSinceCached < 30) {
        console.log('[Geocoding] Cache hit for:', address);
        return { lat: cached.lat, lng: cached.lng, address: cached.address, cachedAt: cachedAt || undefined };
      }
    }

    // 2. Try Photon (limit=3 so we get the best city-level result, not just a street)
    console.log('[Geocoding] Trying Photon for:', address);
    const photonUrl = `https://photon.komoot.io/api/?q=${encodeURIComponent(address)}&limit=3&lang=nl`;
    const photonResponse = await fetch(photonUrl);

    let result: GeocodingResult | null = null;

    let shouldFallback = false;

    if (photonResponse.ok) {
      const photonData = await photonResponse.json();
      if (photonData.features && photonData.features.length > 0) {
        // Prefer city/town/village features over streets when multiple results come back
        const cityFeature = photonData.features.find((f: any) =>
          ['city', 'town', 'village', 'municipality', 'county'].includes(f.properties?.type)
        ) || photonData.features[0];
        const coords: number[] = cityFeature.geometry?.coordinates ?? [];
        const [lng, lat] = coords;
        if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
          result = { lat, lng, address: buildAddressFromPhoton(cityFeature.properties) };
          console.log('[Geocoding] Photon result for:', address, '→', result.address);
        }
      }
    } else if (PHOTON_FALLBACK_STATUSES.has(photonResponse.status)) {
      // Only fall back to Nominatim for rate-limiting / bad-request errors.
      // For 5xx server errors we skip the fallback — if Photon's servers are down,
      // Nominatim is unlikely to help and we avoid unnecessary extra traffic.
      console.warn(`[Geocoding] Photon ${photonResponse.status} for "${address}" — trying Nominatim fallback`);
      shouldFallback = true;
    } else {
      console.warn(`[Geocoding] Photon server error ${photonResponse.status} for "${address}" — skipping fallback`);
    }

    // 3. Nominatim fallback — only when Photon gave us nothing AND it was a fallback-eligible error
    if (!result && (shouldFallback || photonResponse.ok)) {
      console.log('[Geocoding] Falling back to Nominatim for:', address);
      result = await geocodeViaNominatim(address);
      if (result) {
        console.log('[Geocoding] Nominatim result for:', address, '→', result.address);
      }
    }

    if (!result) {
      console.warn('[Geocoding] No results from any provider for:', address);
      return null;
    }

    // 4. Save to Firestore cache
    await setDoc(cacheRef, {
      lat: result.lat,
      lng: result.lng,
      address: result.address,
      originalQuery: address,
      cachedAt: serverTimestamp(),
      provider: 'photon'
    }).catch(err => console.warn('[Geocoding] Cache write failed (non-critical):', err));

    return result;

  } catch (error) {
    console.error('[Geocoding] Unexpected error:', error);
    return null;
  }
}

/**
 * Reverse geocode: coordinates to address with caching
 * Uses Photon reverse endpoint (CORS-enabled)
 */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  const cacheKey = `${lat.toFixed(6)}_${lng.toFixed(6)}`;
  
  try {
    // 1. Check cache
    const cacheRef = doc(db, 'reverseGeocodingCache', cacheKey);
    const cacheDoc = await getDoc(cacheRef);
    
    if (cacheDoc.exists()) {
      const cached = cacheDoc.data();
      const cachedAt = cached.cachedAt?.toDate();
      const daysSinceCached = cachedAt 
        ? (Date.now() - cachedAt.getTime()) / (1000 * 60 * 60 * 24)
        : 999;
      
      if (daysSinceCached < 30) {
        console.log('[Reverse Geocoding] Using cached result');
        return cached.address;
      }
    }
    
    // 2. Call Photon reverse API
    console.log('[Reverse Geocoding] Fetching from Photon');
    
    const url = `https://photon.komoot.io/reverse?lat=${lat}&lon=${lng}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    
    if (!data.features || data.features.length === 0) {
      return null;
    }
    
    const address = buildAddressFromPhoton(data.features[0].properties);
    
    // 3. Cache result
    await setDoc(cacheRef, {
      address,
      lat,
      lng,
      cachedAt: serverTimestamp(),
      provider: 'photon'
    });
    
    return address;
    
  } catch (error) {
    console.error('[Reverse Geocoding] Error:', error);
    return null;
  }
}

/**
 * Calculate midpoint between two coordinates
 * Pure math - no API call needed
 */
export function calculateMidpoint(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): { lat: number; lng: number } {
  // Convert to radians
  const dLon = (lng2 - lng1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;
  const lng1Rad = lng1 * Math.PI / 180;
  
  // Haversine midpoint formula
  const Bx = Math.cos(lat2Rad) * Math.cos(dLon);
  const By = Math.cos(lat2Rad) * Math.sin(dLon);
  
  const midLat = Math.atan2(
    Math.sin(lat1Rad) + Math.sin(lat2Rad),
    Math.sqrt((Math.cos(lat1Rad) + Bx) * (Math.cos(lat1Rad) + Bx) + By * By)
  ) * 180 / Math.PI;
  
  const midLng = (lng1Rad + Math.atan2(By, Math.cos(lat1Rad) + Bx)) * 180 / Math.PI;
  
  return { lat: midLat, lng: midLng };
}

/**
 * Calculate distance between two coordinates (in km)
 */
export function calculateDistance(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lng2 - lng1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}
